import { format } from 'date-fns/format'
import { startOfMonth } from 'date-fns/startOfMonth'
import { startOfToday } from 'date-fns/startOfToday'
import { subMonths } from 'date-fns/subMonths'

import type { Palette, Theme } from '@mui/material'

import { CategoryIconList } from '@mxenabled/mxui'

import {
  CategoryColors,
  CategoryGuids,
  CategoryIcon,
  DATE_FORMATS_INTL,
  formatDate,
} from '../constants'

import type {
  Category,
  DateRangeCategoryTotals,
  DetailedCategory,
  DetailedCategoryWithTransactions,
  MonthlyAmount,
  MonthlyCategoryTotals,
  Transaction,
  CategoryCopy,
  CategoryTotal,
} from '../types'

const MONTHLY_AVERAGE_LENGTH = 3
const MONTHS_OF_HISTORY = 12

/**
 * Augment categories
 * @param categories - Categories to augment
 * @param monthlyCategoryTotals - Monthly category totals
 * @param currentCategoryTotals - Current month category totals
 * @param copy - Category copy
 */
export const augmentCategories = (
  categories: Category[],
  copy: CategoryCopy,
  currentCategoryTotals: DateRangeCategoryTotals[],
  monthlyCategoryTotals: MonthlyCategoryTotals[],
): DetailedCategory[] => {
  return categories
    .filter((category) => !category.parent_guid)
    .map((category) =>
      getDetailedCategory(
        category,
        copy,
        currentCategoryTotals,
        monthlyCategoryTotals,
        categories.filter((c) => c.parent_guid === category.guid),
      ),
    )
}

export const getCategoryColor = (guid: string, theme: Theme) => {
  const categoryInfo = CategoryIconList.find((c) => c.guid === guid)
  if (!categoryInfo) return theme.palette.categories.uncategorized || ''

  return theme.palette.categories[categoryInfo.colorName as keyof Palette['categories']] || ''
}

export const getCategoryIcon = (guid: string) => {
  return CategoryIconList.find((c) => c.guid === guid)?.icon
}

/**
 * Get an augmented category with additional details
 * @param category - The category to augment
 * @param copy - Category copy
 * @param currentCategoryTotals - Current month category totals
 * @param monthlyCategoryTotals - Past monthly category totals
 * @param subCategories - Subcategories for the specified category (If applicable)
 */
const getDetailedCategory = (
  category: Category,
  copy: CategoryCopy,
  currentCategoryTotals: DateRangeCategoryTotals[],
  monthlyCategoryTotals: MonthlyCategoryTotals[],
  subCategories: Category[] = [],
): DetailedCategory => {
  const guid = category.guid
  const parentGuid = category.parent_guid || guid
  const isParent = guid === parentGuid

  const currentAmount = currentCategoryTotals.find((t) => t.category_guid === guid)?.total || 0
  const monthlyAmounts = getMonthlyAmounts(
    monthlyCategoryTotals.filter((total) => total.category_guid === guid),
  )
  const totalMonthlyAmounts = getMonthlyAmounts(
    monthlyCategoryTotals.filter(
      (total) => total.category_guid === guid || total.top_level_category_guid === guid,
    ),
  )
  const detailedSubCategories = subCategories.map((subCategory) =>
    getDetailedCategory(subCategory, copy, currentCategoryTotals, monthlyCategoryTotals),
  )
  const totalAmount = detailedSubCategories.reduce(
    (total, current) => total + current.currentAmount,
    currentAmount,
  )

  const name = copy.default_categories[category.guid]
    ? copy.default_categories[category.guid].replace(/&amp;/g, '&')
    : category.name

  return {
    ...category,
    currentAmount,
    averageAmount: getAverage(monthlyAmounts),
    color: CategoryColors[parentGuid],
    icon: CategoryIcon[parentGuid],
    isTransfer: parentGuid === CategoryGuids.TRANSFER,
    monthlyAmounts,
    name,
    subCategories: detailedSubCategories,
    totalAmount: isParent ? totalAmount : 0,
    totalAverageAmount: getAverage(totalMonthlyAmounts),
    totalMonthlyAmounts: isParent ? totalMonthlyAmounts : [],
  }
}
/**
 * get the transactions belonging to parent categories
 * @param detailedCategories - detailed categories from the category store
 * @param transactions - sortedTransactions from the transaction store
 */
export const getDetailedCategoryTransactions = (
  detailedCategories: DetailedCategory[],
  transactions: Transaction[],
): DetailedCategoryWithTransactions[] => {
  return detailedCategories.map((category) => {
    const categoryTransactions = transactions.filter(
      (transaction) =>
        transaction.category_guid === category.guid ||
        transaction.top_level_category_guid === category.guid,
    )

    return {
      ...category,
      transactions: categoryTransactions,
    }
  })
}

/**
 * Get the average amount based on the number of months (numMonths) requested
 * @param monthlyAmounts - Monthly amounts to calculate and average from
 * @param numMonths - Number of months to base the average on (DEFAULT: 3)
 */
const getAverage = (
  monthlyAmounts: MonthlyAmount[],
  numMonths: number = MONTHLY_AVERAGE_LENGTH,
): number => {
  const monthsBack = numMonths > history.length ? history.length : numMonths

  return Math.ceil(
    Math.max(
      0,
      monthlyAmounts.slice(-monthsBack).reduce((total, current) => total + current.amount, 0) /
        monthsBack,
    ),
  )
}

/**
 * Get the last 12 months of amounts for a category
 * @param categoryTotals - Monthly category totals
 */
const getMonthlyAmounts = (categoryTotals: MonthlyCategoryTotals[]): MonthlyAmount[] => {
  const monthlyAmounts: MonthlyAmount[] = []
  const today = startOfToday()
  for (let i = 0; i <= MONTHS_OF_HISTORY; i++) {
    const historyDate = startOfMonth(subMonths(today, MONTHS_OF_HISTORY - i))
    const monthlyTotals = categoryTotals.filter(
      (total) => total.year_month === Number(format(historyDate, 'yyyyMM')),
    )
    const totalAmount = monthlyTotals.reduce((acc, curr) => acc + curr.total, 0)

    monthlyAmounts.push({
      amount: totalAmount,
      isoDate: format(historyDate, 'yyyy-MM-dd'),
      label: formatDate(historyDate, DATE_FORMATS_INTL.MONTH_YEAR),
    })
  }

  return monthlyAmounts
}

export const findCategory = (
  categories: Category[],
  categoryGuid: string | undefined,
): Category | undefined => {
  return categories.find((c) => c.guid === categoryGuid)
}

export const findDetailedCategory = (
  detailedCategories: DetailedCategory[],
  guid: string | undefined,
): DetailedCategory | undefined => {
  return detailedCategories.find((dc) => dc.guid === guid)
}

export const findDetailedSubCategory = (
  detailedCategories: DetailedCategory[],
  guid: string | undefined,
): DetailedCategory | undefined => {
  const parent = detailedCategories.find((dc) => dc.subCategories.find((sc) => sc.guid === guid))
  if (!parent) return undefined

  return parent.subCategories.find((sc) => sc.guid === guid)
}

export const findCategoryTotalsByParent = (
  categories: Category[],
  dateRangeTotals: DateRangeCategoryTotals[],
): CategoryTotal[] => {
  const categoryTotals = [] as CategoryTotal[]

  // Iterate over the category data from BE and roll up all categories into parent categories
  // acc is the accumulated category totals of type CategoryTotal that is initialized as an empty []
  // curr is the current DateRangeCategoryTotal

  for (const dateRangeTotal of dateRangeTotals) {
    const category = findCategory(categories, dateRangeTotal.category_guid)
    if (!category) continue

    const parentGuid = category.parent_guid ?? category.guid
    const parentCategory = findCategory(categories, parentGuid)
    if (!parentCategory) continue

    const categoryTotal = {
      amount: dateRangeTotal.total,
      category,
      guid: category.guid,
      subCategoryTotals: [],
    }

    const parentCategoryTotal = categoryTotals.find((ct) => ct.guid === parentGuid)
    if (parentCategoryTotal) {
      parentCategoryTotal.amount += dateRangeTotal.total
      parentCategoryTotal.subCategoryTotals.push(categoryTotal)
    } else {
      categoryTotals.push({
        amount: dateRangeTotal.total,
        category: parentCategory,
        guid: parentGuid,
        subCategoryTotals: [categoryTotal],
      })
    }
  }

  return categoryTotals
}

export const isIncome = (categories: Category[], guid: string) => {
  const category = categories.find((c) => c.guid === guid)
  if (!category) return false

  return category.guid === CategoryGuids.INCOME || category.parent_guid === CategoryGuids.INCOME
}
